import type { EmbeddedStudioSafetyRules } from "../../rules/types.js";
import type { EmbedConfig } from "../../search/rules/params.js";
import type { EmbedConfigFieldKey } from "./snippet.js";

/**
 * EmbedSafetyRules is the embed safety-rules slice a wizard gates its controls against.
 *
 * DERIVED from the published wire shape rather than restated, so a rule the platform adds
 * cannot be silently absent here. Two deliberate differences from it, and each is about what a
 * HOST can actually hand over:
 *
 * `boundaries` is omitted. The geofence gates WHERE tiles may load, which the tile and embed
 * endpoints enforce server-side, and no control in a wizard is gated on it. Leaving it out is
 * also what keeps this satisfied by a host's own parse and by the wire record alike, since the
 * two spell a geofence differently.
 *
 * Every remaining field is OPTIONAL, which is the same statement the readers below already
 * make: an absent rule restricts nothing. A host reads these from wherever its own account
 * model keeps them (which is not in the public contract), so it hands over what it has, and
 * the whole object is absent for a host that has no such notion at all.
 */
export type EmbedSafetyRules = Partial<Omit<EmbeddedStudioSafetyRules, "boundaries">>;

/**
 * gatedBooleanRules maps each boolean embed field the organization can lock down to the
 * safety_rules flag that governs it. A field is restricted when its governing rule
 * is present and false: the publisher disabled that capability for embeds, so the
 * wizard must not let an embed turn it back on. Street View, navigation, the
 * preferences control, and the download control are NOT here: they carry no
 * safety-rules flag (the server does not gate them per embed), so the wizard leaves
 * them freely configurable. Neither are the two collection-content rules: they clamp
 * what a collection-target embed may expose, the server applies them, and no embed
 * spells them, so there is no control here for them to gate. Keyed by
 * EmbedConfigFieldKey so a control resolves its rule in one lookup.
 */
const gatedBooleanRules = {
  withOverlaysControl: "with_overlays_control",
  withProfileAnalysis: "with_profile_analysis",
  withProfileElevation: "with_profile_elevation",
} as const satisfies Partial<Record<EmbedConfigFieldKey, keyof EmbedSafetyRules>>;

/**
 * GatedBooleanKey is one boolean embed field a safety rule can restrict, derived
 * from gatedBooleanRules so the gating map is the single source of truth.
 */
export type GatedBooleanKey = keyof typeof gatedBooleanRules;

/** isGatedBooleanKey narrows a field key to one a safety rule can restrict. */
const isGatedBooleanKey = (key: string): key is GatedBooleanKey =>
  Object.hasOwn(gatedBooleanRules, key);

/**
 * isFieldRestricted reports whether a boolean embed field is locked off by the
 * organization's safety rules: true only when the field is one of the gated booleans AND its
 * governing safety rule is present and explicitly false. With no safety rules at all,
 * or for a field that carries no rule, nothing is restricted. A restricted field's
 * toggle renders disabled and forced off: the organization has withheld that capability from
 * embeds, so the publisher cannot enable it here.
 *
 * @param key The field a control sets.
 * @param safetyRules The organization's safety rules, or undefined when it states none.
 */
export const isFieldRestricted = (
  key: EmbedConfigFieldKey,
  safetyRules: EmbedSafetyRules | undefined,
): boolean => {
  if (!safetyRules || !isGatedBooleanKey(key)) {
    return false;
  }

  return safetyRules[gatedBooleanRules[key]] === false;
};

/**
 * ALLOWLIST_WILDCARD is the entry that states the whole catalog in an allowlist, a display list
 * and the enforced cap alike: the one `allowlistUnrestricted` reads beside an empty list, the one
 * an embed's URL writes for a list that restricts nothing, and the one the layer options read in
 * the organization's cap. Declared once, so the readers and the writer of the convention cannot
 * spell it apart.
 */
export const ALLOWLIST_WILDCARD = "*";

/**
 * allowlistUnrestricted reports whether a DISPLAY allowlist, an embed's `with_overlays` or
 * `with_terrains`, offers the whole catalog: it does when EMPTY or holding `"*"` anywhere.
 *
 * The ENFORCED cap, `safety_rules.with_overlays`, is never read through this. The tile API
 * reads a `"*"` anywhere in it the same way, but reads an EMPTY cap as refusing every overlay,
 * so this reading would offer a catalog whose tiles are then refused.
 *
 * Named for the SHAPE rather than for one of the things it caps. The convention is the
 * platform's, and it applies identically to the terrain list and the overlay list: a name
 * mentioning overlays would read as a mistake at the terrain call site while behaving
 * correctly, which is the worse of the two ways to be wrong about a name.
 */
export const allowlistUnrestricted = (allowed: readonly string[]): boolean =>
  allowed.length === 0 || allowed.includes(ALLOWLIST_WILDCARD);

/**
 * AllowlistVariant is which of the two allowlists a list is read as, since the two read an
 * empty list apart.
 *
 * - `"cap"` is the ENFORCED list, an organization's `safety_rules.with_overlays`: the tile API
 *   refuses whatever it does not name, so an empty one refuses everything.
 * - `"display"` is a list that narrows what an embed shows, its `with_overlays` or
 *   `with_terrains`, inside whatever the cap still allows: an empty one narrows nothing.
 */
export type AllowlistVariant = "cap" | "display";

/**
 * AllowlistMode is the choice an allowlist states: `"all"` of the catalog, `"none"` of it, or
 * the `"specific"` set it names. A display list never states `"none"`.
 */
export type AllowlistMode = "all" | "none" | "specific";

/**
 * allowlistMode reads an allowlist as the choice it states, under either variant.
 *
 * A list holding {@link ALLOWLIST_WILDCARD} anywhere is `"all"` under both: that is how the
 * tile API reads a cap and how a display list is read ({@link allowlistUnrestricted}). An empty
 * list is `"none"` for a cap, a real refusal of everything, and `"all"` for a display list,
 * which narrows nothing. Any other list is `"specific"`.
 *
 * It is the reading an allowlist control shows its segment from, so the wildcard is never a
 * value a publisher types, and the one statement of how the two variants differ.
 *
 * @param allowed The list as stored: slugs, with or without the wildcard.
 * @param variant Which allowlist the list is.
 *
 * @example
 * ```ts
 * import { allowlistMode } from "@mountaya/studio";
 *
 * allowlistMode([], "cap"); // "none": the organization allows no overlay
 * allowlistMode([], "display"); // "all": the embed narrows nothing
 * allowlistMode(["aspectslope", "*"], "cap"); // "all"
 * ```
 */
export const allowlistMode = (
  allowed: readonly string[],
  variant: AllowlistVariant,
): AllowlistMode => {
  if (allowed.includes(ALLOWLIST_WILDCARD)) {
    return "all";
  }

  if (allowed.length === 0) {
    return variant === "cap" ? "none" : "all";
  }

  return "specific";
};

/**
 * allowlistForMode writes a choice as the list that states it, the inverse of
 * {@link allowlistMode} for every mode a variant offers.
 *
 * `"all"` is the wildcard alone for a cap, where an empty list would refuse everything, and
 * the empty list for a display list, which already reads so and stays equal to an untouched
 * default. `"none"` is the empty list. `"specific"` is a copy of `selection`.
 *
 * A display list has no `"none"`: written for one, the empty list reads back as `"all"`, so a
 * control offers that mode for a cap alone. A specific set that names nothing is the empty
 * list too, and reads as its variant reads one.
 *
 * @param mode The choice to state.
 * @param variant Which allowlist the list is.
 * @param selection The slugs a `"specific"` choice names. Read for that mode alone.
 * @returns A new list.
 */
export const allowlistForMode = (
  mode: AllowlistMode,
  variant: AllowlistVariant,
  selection: readonly string[],
): string[] => {
  if (mode === "all") {
    return variant === "cap" ? [ALLOWLIST_WILDCARD] : [];
  }

  return mode === "none" ? [] : [...selection];
};

/**
 * clampConfigToSafetyRules forces every safety-restricted boolean field off in the
 * live embed config, so a control the organization locked down can never carry an enabled
 * value into the preview or the snippet, even if the organization's default_props left it on
 * (default_props and safety_rules are independent; the server strips a restricted
 * capability for visitors, and the wizard mirrors that here). It only ever turns a
 * restricted boolean false, never on, and leaves every other field untouched, the
 * overlay allowlist included, which stays deselectable rather than force-stripped.
 *
 * @param config The resolved embed config.
 * @param safetyRules The organization's safety rules, or undefined when it states none.
 * @returns The same reference when nothing changed, so a clamped seed and a clamped patch stay
 *   referentially stable when no rule bites; a new config otherwise.
 */
export const clampConfigToSafetyRules = (
  config: EmbedConfig,
  safetyRules: EmbedSafetyRules | undefined,
): EmbedConfig => {
  if (!safetyRules) {
    return config;
  }

  let next = config;
  for (const key of Object.keys(gatedBooleanRules).filter(isGatedBooleanKey)) {
    if (isFieldRestricted(key, safetyRules) && next[key]) {
      next = { ...next, [key]: false };
    }
  }

  return next;
};
